import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../services/AuthContext';
import { fetchWithAuth } from '../services/AuthService';
import { Button } from 'react-bootstrap';
import Filters from './Filters';
import PlaySlider from './PlaySlider';
import PieMorphing from './PieMorphing';
import LineChartComponent from './LineChartComponent';
import Analyse from "./Analyse";
import { PieChart, GraphUp } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import '../css/Result.css';

const COLORS = ['#C11D24', '#DA5E20', '#E0B614', '#29A648', '#029FB2', '#375A9C', '#6F3F89'];

const labels = [
  { labelLeft: "Anger", labelRight: "Security" },
  { labelLeft: "Frustration", labelRight: "Plenitude" },
  { labelLeft: "Sadness", labelRight: "Joy" },
  { labelLeft: "Loneliness", labelRight: "Self-love" },
  { labelLeft: "Bad luck", labelRight: "Alignement" },
  { labelLeft: "Hate", labelRight: "Peace" },
  { labelLeft: "Melancholia", labelRight: "Inner Faith" },
];

const translationKeys = [
  'stats.line.red',
  'stats.line.orange',
  'stats.line.yellow',
  'stats.line.green',
  'stats.line.cyan',
  'stats.line.blue',
  'stats.line.purple'
];

const Statistics = () => {
  const { isUserAuthenticated, authLoading } = useAuth();
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(null);
  const [filteredMinDate, setFilteredMinDate] = useState(null);
  const [filteredMaxDate, setFilteredMaxDate] = useState(null);
  const [chartType, setChartType] = useState("pie");
  const { t } = useTranslation();

  const translatedLabels = translationKeys.map(key => ({
    labelLeft: '',
    labelRight: t(key) 
  }));

  useEffect(() => {
    if (authLoading) return;

    if (!isUserAuthenticated) {
      setLoading(false);
      setError("Vous devez être connecté pour voir l'historique.");
      return;
    }  
    
    const fetchResults = async () => {
        try {
          //console.log("Open stats");
          const response = await fetchWithAuth('/api/results?source=stats');
          if (!response.ok) throw new Error('Erreur lors du chargement des résultats.');
          
          const data = await response.json();
          
          setResults(data);
          setFilteredResults(data); // au début : tout afficher

          if (data.length > 0) {
            const dates = data.map(r => new Date(r.createdAt).getTime());
            const min = Math.min(...dates);
            const max = Math.max(...dates);

            setCurrentTime(min);       
            setFilteredMinDate(min);   
            setFilteredMaxDate(max);
          } 
        } catch (err) {
          console.error(err);
          setError("Impossible de charger les résultats.");
        } finally {
          setLoading(false);
        }
      };

      fetchResults();
  }, [isUserAuthenticated, authLoading]);

  useEffect(() => {
      if (filteredMinDate !== null) {
        setCurrentTime(filteredMinDate);
      }
    }, [filteredMinDate, filteredMaxDate]);

  const [minDate, maxDate] = useMemo(() => {
      if (results.length === 0) return [null, null];
      const dates = results.map(r => new Date(r.createdAt).getTime());
      return [Math.min(...dates), Math.max(...dates)];
    }, [results]);

  const handleDateRangeChange = ([startDate, endDate]) => {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      const filtered = results.filter((r) => {
        const date = new Date(r.createdAt);
        return date >= start && date <= end;
      });

      setFilteredResults(filtered);

      setFilteredMinDate(start.getTime());
      setFilteredMaxDate(end.getTime());
    };

  const isPieChart = chartType === "pie";

  if (authLoading) return <p>Vérification de la session...</p>;
  if (loading) return <p>Chargement...</p>;
  if (error) return <p>{error}</p>;
  if (results.length === 0) return <p>{t('stats.empty')}</p>;

  return (
    <div className="statistics-content">
      <div className="filters-content">
      {minDate && maxDate && (
        <Filters
          minDate={minDate}
          maxDate={maxDate}
          onDateRangeChange={handleDateRangeChange}
        />
      )}
      </div>
      <div className="playslider-content">
      {filteredMinDate && filteredMaxDate && isPieChart && (
        <PlaySlider 
          // minDate={filteredMinDate} 
          // maxDate={filteredMaxDate} 
          results={filteredResults} 
          value={currentTime}
          onChange={setCurrentTime}   
        />
      )}
      <div className='toggle-graph-view'>
        <Button onClick={() => setChartType(isPieChart ? "line" : "pie")}>
          {isPieChart ? <GraphUp /> : <PieChart />}
        </Button>
        </div>
      </div>

      <div className="pie-morphing-content">
        {filteredResults.length > 0 && currentTime && (() => {
          const sortedResults = [...filteredResults].sort(
            (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
          );
          const closestResult = filteredResults.reduce((prev, curr) => {
            const currTime = new Date(curr.createdAt).getTime();
            return Math.abs(currTime - currentTime) <
              Math.abs(new Date(prev.createdAt).getTime() - currentTime)
              ? curr
              : prev;
          });

          const currentIndex = sortedResults.findIndex(r => r._id === closestResult._id);
          const prevScan = currentIndex > 0 ? sortedResults[currentIndex - 1].answers : null;
          const firstScan = filteredResults[0].answers;

          return (
            <>
              {isPieChart ? (
                <div className='stat-pie-chart'>
                <PieMorphing
                  targetAnswers={closestResult.answers}
                  targetScoring={closestResult.scoring}
                /></div>
              ) : (
                <div className='stat-line-chart'>
                <LineChartComponent
                  fullSequence={filteredResults.map(r => r.answers)}
                  filteredResults={filteredResults}
                  labels={translatedLabels}
                  colors={COLORS}
                /></div>
              )}

              {isPieChart && (
              <Analyse
                currentScan={closestResult.answers}
                prevScan={prevScan}
                firstScan={firstScan}
                fullSequence={filteredResults.map(r => r.answers)}
                labels={labels}
              />)}
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default Statistics;
